import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaQuoteLeft } from 'react-icons/fa';
import { api } from '../../api/client';
import { StarRow } from '../ApartmentReviews/ApartmentReviews';
import './testimonials.css';

/** One request worth of reviews for the home carousel (API max 50). */
const FETCH_PER_PAGE = 40;
const SCROLL_GAP_PX = 12;
const AUTO_SCROLL_MS = 4200;

function initialsFromName(name) {
  const parts = String(name || 'Guest')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return 'G';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function ReviewAvatar({ name, url }) {
  const [broken, setBroken] = useState(false);
  const label = `${name} avatar`;
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={label}
        width={72}
        height={72}
        loading="lazy"
        decoding="async"
        className="testimonial-avatar"
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <span className="testimonial-avatar testimonial-avatar--placeholder" aria-hidden>
      <span className="testimonial-avatar-initials">{initialsFromName(name)}</span>
    </span>
  );
}

function apartmentSubtitle(apt) {
  if (!apt) return '';
  const bits = [];
  if (apt.property_name) bits.push(apt.property_name);
  if (apt.city) bits.push(apt.city);
  return bits.length ? bits.join(' · ') : '';
}

function ReviewCard({ rev }) {
  const author = rev.author || {};
  const apt = rev.apartment || null;
  const aptPath = apt?.id != null ? `/apartments/${apt.id}` : '/rooms';
  const subtitle = apartmentSubtitle(apt);
  return (
    <article className="testimonial-block">
      <FaQuoteLeft className="testimonial-icon" aria-hidden />
      <div className="testimonial-stars-wrap">
        <StarRow value={Number(rev.rating) || 0} size={15} />
      </div>
      <p className="testimonial-quote">{rev.comment}</p>
      <div className="testimonial-author">
        <ReviewAvatar name={author.name} url={author.avatar_url} />
        <div className="testimonial-author-text">
          <h3 className="testimonial-name">{author.name || 'Guest'}</h3>
          {subtitle ? <span className="testimonial-location">{subtitle}</span> : null}
          <Link to={aptPath} className="testimonial-apartment-link">
            {apt?.name ? `View ${apt.name}` : 'View listing'}
            <span className="testimonial-apartment-link-arrow" aria-hidden>
              →
            </span>
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function Testimonials() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const scrollerRef = useRef(null);
  const pauseHoverRef = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await api.get(`/reviews?per_page=${FETCH_PER_PAGE}&page=1`, null);
      const list = Array.isArray(res.data) ? res.data : [];
      setRows(list);
      setMeta(res.meta || null);
    } catch (e) {
      setRows([]);
      setMeta(null);
      setError(e.message || 'Could not load reviews.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (rows.length === 0) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    const tick = () => {
      if (pauseHoverRef.current) return;
      const el = scrollerRef.current;
      if (!el) return;
      const first = el.querySelector('.testimonial-block');
      if (!first) return;
      const step = first.offsetWidth + SCROLL_GAP_PX;
      const maxScroll = el.scrollWidth - el.clientWidth;
      if (maxScroll <= 4) return;
      if (el.scrollLeft >= maxScroll - 4) {
        el.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        el.scrollBy({ left: step, behavior: 'smooth' });
      }
    };

    const id = window.setInterval(tick, AUTO_SCROLL_MS);
    return () => window.clearInterval(id);
  }, [rows.length]);

  const total = meta?.total != null ? Number(meta.total) : rows.length;
  const showCarouselMeta = !loading && !error && rows.length > 0;

  return (
    <div className="testimonials">
      <section aria-labelledby="testimonials-heading">
        <div className="testimonials-header">
          <span className="testimonials-eyebrow">Guest reviews</span>
          <h2 id="testimonials-heading">From stays across our apartments</h2>
          <p>
            Star ratings and comments left by guests after their bookings — each review is tied to a real listing on Luxistt.
          </p>
        </div>

        {loading ? (
          <div className="testimonials-carousel-shell" aria-busy="true" aria-label="Loading reviews">
            <div className="testimonials-carousel testimonials-carousel--skeleton">
              <div className="testimonials-track">
                {[1, 2, 3, 4, 5].map((k) => (
                  <div key={k} className="testimonial-block testimonial-block--skeleton">
                    <div className="testimonial-skeleton-shimmer" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}

        {!loading && error ? (
          <p className="testimonials-error" role="alert">
            {error}
          </p>
        ) : null}

        {!loading && !error && rows.length === 0 ? (
          <div className="testimonials-empty">
            <p>No guest reviews are published yet. When you stay with us, your feedback will appear here for future guests.</p>
            <Link to="/rooms" className="testimonials-empty-cta">
              Browse apartments
            </Link>
          </div>
        ) : null}

        {!loading && !error && rows.length > 0 ? (
          <div
            className="testimonials-carousel-shell"
            onMouseEnter={() => {
              pauseHoverRef.current = true;
            }}
            onMouseLeave={() => {
              pauseHoverRef.current = false;
            }}
          >
            <div className="testimonials-carousel-fade testimonials-carousel-fade--left" aria-hidden />
            <div
              ref={scrollerRef}
              className="testimonials-carousel"
              tabIndex={0}
              role="region"
              aria-roledescription="carousel"
              aria-label="Guest reviews. Auto-advances; pause by hovering or focusing inside."
            >
              <div className="testimonials-track">
                {rows.map((rev) => (
                  <ReviewCard key={rev.id} rev={rev} />
                ))}
              </div>
            </div>
            <div className="testimonials-carousel-fade testimonials-carousel-fade--right" aria-hidden />
            {showCarouselMeta ? (
              <p className="testimonials-carousel-meta">
                {total} guest review{total === 1 ? '' : 's'}
                {total > rows.length ? ` · showing ${rows.length}` : ''}
              </p>
            ) : null}
          </div>
        ) : null}
      </section>
    </div>
  );
}
